/**
 * STORY-R1: optional first-entry story. No game state or network effects.
 *
 * R224(레드팀 P2-4, DESIGN 14.402) — 이 카드는 **세계관과 잃는 규칙**을 말하기로 설계됐는데(index.html 주석) 문장은 세계관뿐이었다.
 * 하드코어에서 처음 죽고 나서 규칙을 알면 배신감이 된다 — 마지막 줄이 잃는 규칙 둘(죽으면 떨군다 · 세계가 끝나면 비워진다)을 말한다.
 * 초반 보호 레벨(콘솔 `firstStepsConfig.safeLevel`, 기본 5)은 설정값이라 숫자 대신 뜻으로 적는다. 도움말 「이 세계의 이야기」도 같은 줄을 읽는다.
 * R228(레드팀 P2-8 ①, DESIGN 14.417) — 세계가 24시간 안으로 남았으면 카드 끝 줄들이 남은 시간·새 세계·다음 세계 보너스를 말한다(`ui/arrival-lines.js`).
 */
export const STORY_LINES = [
  '멸망을 앞둔 세계에 도착했다.',
  '이곳의 주민들은 고향을 지키려 싸우고 있다.',
  '이세계에서 온 용사여.',
  '생명의 돌을 찾아 신전에 바쳐다오.',
  '마지막 날이 오기 전에, 우리를 구해다오.',
  '죽으면 가진 것을 떨구고(처음 몇 레벨은 지켜 준다), 세계가 끝나면 다시 1레벨이 되고 가방은 비워진다. 창고의 이월은 창고에서 확인해 다오.',
];
export const STORY_WELCOME = '우린 이곳에서 태어났어. 고향을 지킬 수 있게 도와줘.';
export const STORY_OFFERING = '생명의 돌을 신전에 봉헌하면 멸망을 늦출 수 있어. 마지막 날이 시작되기 전에 가져와 줘.';
// 묶음 HUD1(DESIGN 14.468, GS0 I6) — 위쪽 이야기 띠는 짧게: 9초 → 6초(첫 10초는 몬스터를 치는 시간이다 · 문장은 그대로, 도움말에서 다시 본다)
export const STORY_DURATION = 6000;

/**
 * 묶음 AA(DESIGN 14.431) — 카드가 아니라 **위쪽 가운데의 얇은 띠**다. 줄마다 제 칸(`.sl`)을 받아 한 줄씩 차례로 떠올랐다 사라진다
 * (`--i`·`--n` 으로 CSS 가 시각을 나눈다 — 전체가 STORY_DURATION 안에 끝난다). 글 내용은 그대로 한 덩어리로 읽힌다(줄 사이에 줄바꿈 글자).
 * DOM 이 없는 검사 픽스처(`story-probe`)에서는 글자만 넣는다. 움직임을 줄인 기기는 CSS 가 모든 줄을 한꺼번에 보인다.
 */
function setLines(el, lines) {
  const doc = el?.ownerDocument;
  if (!doc || typeof el.replaceChildren !== 'function') { el.textContent = lines.join('\n'); return; }
  // 줄마다 머무는 시간은 글자 수에 비례한다(긴 규칙 줄이 짧은 인사 줄과 같은 시간을 받지 않게) — 합이 STORY_DURATION 이다
  const weight = lines.map((line) => 8 + String(line).length);
  const total = weight.reduce((a, b) => a + b, 0) || 1;
  const parts = [];
  let at = 0;
  lines.forEach((line, i) => {
    if (i) parts.push(doc.createTextNode('\n'));
    const span = doc.createElement('span');
    const d = STORY_DURATION * weight[i] / total;
    span.className = 'sl';
    span.style.setProperty('--s', `${Math.round(at)}ms`); span.style.setProperty('--d', `${Math.round(d)}ms`);
    span.textContent = line;
    parts.push(span);
    at += d;
  });
  el.replaceChildren(...parts);
}

/** Dependencies keep actual input/timer/storage behavior independently verifiable. */
export function createStoryIntro({ root, arrival, events = window, storage = () => localStorage,
  schedule = setTimeout, cancel = clearTimeout, onArrival = () => {}, focusGame = () => {} } = {}) {
  const seen = new Set();
  let open = false, epoch = 0, timer = null, arrivalTimer = null, guide = '마을 사람', offering = STORY_OFFERING;
  const find = (id) => root?.querySelector('#' + id);
  function clearArrival() {
    if (arrivalTimer !== null) cancel(arrivalTimer);
    arrivalTimer = null;
    arrival?.classList.remove('on');
  }
  function close({ welcome = true } = {}) {
    const wasOpen = open;
    const hadFocus = root?.contains(root.ownerDocument?.activeElement);
    open = false; epoch++;
    if (timer !== null) cancel(timer);
    timer = null;
    events.removeEventListener('pointerdown', outside, true);
    events.removeEventListener('keydown', key, true);
    root?.classList.remove('on');
    root?.setAttribute('aria-hidden', 'true');
    if (find('story-skip')) find('story-skip').onclick = null;
    if (find('story-start')) find('story-start').onclick = null;
    clearArrival();
    if (wasOpen && welcome) {
      const text = guide + ' — “' + STORY_WELCOME + '” ' + offering;
      if (arrival) {
        arrival.textContent = text;
        arrival.classList.add('on');
        const version = epoch;
        arrivalTimer = schedule(() => { if (version === epoch) clearArrival(); }, 8000);
      }
      onArrival(text);
    }
    if (hadFocus) focusGame();
    return wasOpen;
  }
  function outside(event) {
    // Capture only observes the first input: it still reaches the canvas or clicked button.
    if (open && !root.contains(event.target)) close();
  }
  function key(event) {
    if (!open || event.isComposing) return;
    if (event.key === 'Escape') { event.preventDefault(); close(); return; }
    // Keep browser navigation, typing, and native story buttons intact.
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey ||
        event.target?.closest?.('input,textarea,select,button,summary,a,[contenteditable="true"]')) return;
    if (/^(?:[aqritmh1-6]|Tab|Enter|Arrow(?:Up|Down|Left|Right)| )$/i.test(event.key)) close();
  }
  /**
   * R228(레드팀 P2-8 ①) — `lines`·`welcomeTail` 은 세계 단계에 따라 바뀐 문장이다(`ui/arrival-lines.js` 가 만든다 — 이 모듈은 상태를 안 읽는다).
   * 없으면 원래 문장(`STORY_LINES`·`STORY_OFFERING`).
   */
  function show({ charId, world, replay = false, guideName = '마을 사람', lines = null, welcomeTail = null } = {}) {
    if (!charId || !root || !find('story-world') || !find('story-lines') || !find('story-start') || !find('story-skip')) return false;
    const storageKey = 'unige_intro_' + charId;
    if (!replay) {
      if (seen.has(charId)) return false;
      try { if (storage()?.getItem(storageKey)) { seen.add(charId); return false; } } catch { /* memory fallback */ }
    }
    close({ welcome: false });
    guide = guideName;
    offering = typeof welcomeTail === 'string' && welcomeTail ? welcomeTail : STORY_OFFERING;
    const n = Number(world?.n);
    find('story-world').textContent = Number.isSafeInteger(n) && n > 0 ? '제 ' + n + ' 세계 · 이방인의 도착' : '이방인의 도착';
    setLines(find('story-lines'), Array.isArray(lines) && lines.length ? lines : STORY_LINES);
    root.classList.add('on');
    root.setAttribute('aria-hidden', 'false');
    open = true;
    // Write at presentation, so a reload before dismissal cannot resurrect the card.
    seen.add(charId);
    try { storage()?.setItem(storageKey, '1'); } catch { /* same-tab memory still works */ }
    find('story-skip').onclick = () => close();
    find('story-start').onclick = () => close();
    events.addEventListener('pointerdown', outside, true);
    events.addEventListener('keydown', key, true);
    const version = epoch;
    timer = schedule(() => { if (version === epoch) close(); }, STORY_DURATION);
    return true;
  }
  return { show, close, destroy: () => close({ welcome: false }) };
}
